import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { PageHero, CTABand } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { EditorialMarquee } from "@/components/Marquee";
import { services } from "@/data/products";

export default function Services() {
  return (
    <div data-testid="services-page">
      <PageHero
        overline="Our Services"
        lines={["Six pillars of", "digital growth"]}
        copy="A manifesto for small business empowerment — from secure payments to bulk-buying power. Each service stands alone; together they transform how you trade."
        testid="services-hero"
      />

      <section className="mx-auto max-w-7xl px-6 pb-16 md:px-12">
        {services.map((s, i) => {
          const reversed = i % 2 === 1;
          return (
            <Reveal key={s.slug} delay={0.05}>
              <div
                data-testid={`service-chapter-${i}`}
                className="grid items-center gap-10 border-t border-neutral-200 py-20 lg:grid-cols-12"
              >
                <div
                  className={`lg:col-span-6 ${reversed ? "lg:order-2" : ""}`}
                >
                  <div className="flex items-start gap-8">
                    <span className="hidden font-display text-8xl font-extrabold leading-none text-outline-violet sm:block lg:sticky lg:top-32">
                      {s.number}
                    </span>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.24em] text-das-deep">
                        {s.title}
                      </p>
                      <h2 className="mt-4 font-display text-4xl font-medium leading-[1.1] tracking-tight text-das-ink sm:text-5xl">
                        {s.headline}
                      </h2>
                      <p className="mt-6 max-w-lg text-base leading-relaxed text-neutral-600">
                        {s.copy}
                      </p>
                      <ul className="mt-8 grid max-w-lg gap-3 sm:grid-cols-2">
                        {s.points.map((pt) => (
                          <li key={pt} className="flex items-center gap-3 text-sm font-semibold text-neutral-700">
                            <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-das-violet/10">
                              <Check className="h-3.5 w-3.5 text-das-deep" />
                            </span>
                            {pt}
                          </li>
                        ))}
                      </ul>
                      <Link
                        to="/consultation"
                        data-testid={`service-cta-${i}`}
                        className="group mt-9 inline-flex items-center gap-2 font-display text-base font-semibold text-das-deep"
                      >
                        Enquire about {s.title.toLowerCase()}
                        <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" />
                      </Link>
                    </div>
                  </div>
                </div>
                <div className={`lg:col-span-6 ${reversed ? "lg:order-1" : ""}`}>
                  <div className="group relative overflow-hidden rounded-[2.5rem] border border-neutral-200">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="aspect-[16/11] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <span className="absolute bottom-5 left-5 rounded-full bg-white/85 px-4 py-1.5 font-display text-xs font-bold uppercase tracking-[0.18em] text-das-deep backdrop-blur">
                      Chapter {s.number}
                    </span>
                  </div>
                </div>
              </div>
            </Reveal>
          );
        })}
      </section>

      <EditorialMarquee />

      <CTABand
        title="Let's design your growth stack."
        copy="One conversation is all it takes to map the services that fit your business — and the ones you'll grow into."
        testid="services-cta-band"
      />
    </div>
  );
}
